import React from 'react';
import { TimelineItemType } from 'types/TimelineTypes';
import { UserType } from '../../views/users/UserTypes';
import { useTranslation } from 'react-i18next';
import { useModal } from 'components/modals/ModalContext';
import { translateTimelineUpdates } from 'internationalization/timelineTranslation';
import { formatTimelineDate } from 'services/utils/dateTimeUtils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowRight } from '@fortawesome/free-solid-svg-icons'
import JobModal from '../../views/jobs/JobModal';
import { Link } from 'react-router-dom';
import { renderTicketStatusBadge } from '../../views/tickets/TicketStatusBadge';
import '../../style/scss/timeline.scss';

interface TimelineUpdateProps {
    placement: 'summary' | 'page';
    item: TimelineItemType;
    createdBy?: UserType | null;
}

const TimelineUpdate: React.FC<TimelineUpdateProps> = ({ placement, item, createdBy }) => {
    const { t } = useTranslation();
    const { initializeModal } = useModal();
    const isCreationEvent = item.update && item.update.updated_field === 'created' && !item.update.previous_value;

    // A job created through the field service app renders its own full sentence
    const isMobileCreationEvent = item.update && item.update.updated_field === 'created_mobile';

    // Format the date
    const formattedDate = formatTimelineDate(item.ordering_date, false);

    // Open the job of a clicked job badge
    const handleOpenJob = (jobId: number) => {
        initializeModal(
            <JobModal itemId={jobId} />, {
                itemId: jobId,
                modalSize: 'large'
            }
        );
    };

    // Get the created jobs from a jobs created event
    const getCreatedJobs = (): { id: number; number: string }[] => {
        try {
            const parsedJobs = JSON.parse(item.update?.new_value || '');
            return Array.isArray(parsedJobs) ? parsedJobs : [];
        } catch (error) {
            return [];
        }
    };

    // Translate the timeline updates strings
    if (item.update) {
        const timelinePageType = item.update.timeline_page_type;
        const updatedField = item.update.updated_field || '';
        const newValue = item.update.new_value || '';
        const previousValue = item.update.previous_value || '';

        // Render an object created from a ticket with a link to the ticket and its status badge
        if (updatedField === 'created_from_ticket') {
            const linkedTicket = item.update.linked_ticket;
            return (
                <div className='timeline-item'>
                    {placement === 'page' && (
                        <div className="marker-container">
                            <div className="small-marker"></div>
                        </div>
                    )}
                    <div className="update-content">
                        <p className='update-text timeline-ticket-link'>
                            {t('timeline.update.general.created_from_ticket_label')}
                            {linkedTicket ? (
                                <>
                                    <Link to={`/tickets/${linkedTicket.id}`} className='timeline-ticket-link-name'>
                                        {t('ticket.general.ticket_title', { number: linkedTicket.number })}
                                    </Link>
                                    {renderTicketStatusBadge(linkedTicket.status, false, true)}
                                </>
                            ) : (
                                <span> {t('ticket.conversation.linked_item_removed_label')}</span>
                            )}
                        </p>
                        <div>
                            <p className='update-info'>
                                {formattedDate}
                                {createdBy && createdBy.full_name && (
                                    <>
                                        <span style={{ margin: '0 5px' }}>•</span>
                                        <span>{createdBy.full_name}</span>
                                    </>
                                )}
                            </p>
                        </div>
                    </div>
                </div>
            );
        }

        // Render the created jobs of a contract as clickable job badges
        if (timelinePageType === 'contract' && updatedField === 'jobs_created') {
            const createdJobs = getCreatedJobs();

            if (createdJobs.length > 0) {
                return (
                    <div className='timeline-item'>
                        {placement === 'page' && (
                            <div className="marker-container">
                                <div className="small-marker"></div>
                            </div>
                        )}
                        <div className="update-content">
                            <p className='update-text'>
                                {createdJobs.length === 1
                                    ? t('contract.timeline.job_created_label')
                                    : t('contract.timeline.jobs_created_label')
                                }:
                                {createdJobs.map(createdJob => (
                                    <span key={createdJob.id}
                                          className='timeline-job-badge'
                                          onClick={() => handleOpenJob(createdJob.id)}>
                                        {createdJob.number}
                                    </span>
                                ))}
                            </p>
                            <div>
                                <p className='update-info'>
                                    {formattedDate}
                                </p>
                            </div>
                        </div>
                    </div>
                );
            }
        }
    
        const { translatedFieldName, translatedNewValue, translatedPreviousValue } = translateTimelineUpdates(
            timelinePageType,
            updatedField,
            newValue,
            previousValue,
        );

        return (
            <div className='timeline-item'>
                {placement === 'page' && (
                    <div className="marker-container">
                        <div className="small-marker"></div>
                    </div>
                )}
                <div className="update-content">
                    {item.update && (
                        <>
                            {isMobileCreationEvent ? (
                                // Render the full mobile creation sentence without a value
                                <p className='update-text'>
                                    {t(`${timelinePageType}.backend_field.created_mobile`)}
                                </p>
                            ) : isCreationEvent ? (
                                // Render a creation label, e.g. 'Product created'
                                <p className='update-text'>
                                    {t(`${timelinePageType}.page_type`)} {t('timeline.update.general.created_label')}
                                </p>
                            ) : (
                                <p className='update-text'>
                                    {t(translatedFieldName)}:
                                    {item.update.previous_value &&
                                        <span> {t(translatedPreviousValue) || item.update.previous_value} <FontAwesomeIcon icon={faArrowRight} className='update-arrow' /> </span>
                                    }
                                    <span> {t(translatedNewValue) || item.update.new_value || t('timeline.update.general.empty_label')}</span>
                                </p>
                            )}
                        </>
                    )}
                    <div>
                        <p className='update-info'>
                            {formattedDate}
                            {createdBy && createdBy.full_name && (
                                <>
                                    <span style={{ margin: '0 5px' }}>•</span>
                                    <span>{createdBy.full_name}</span>
                                </>
                            )}
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    return null;
};

export default TimelineUpdate;